import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  systemAuditActionSchema,
  type SystemAuditAction,
  type SystemAuditEntry,
} from '@nbh/shared';
import { propertyTimeZone } from '../../config/property';
import { adminApi } from '../../lib/adminApi';

/** One screenful of history. The contract caps the page at 200 either way. */
const PAGE_SIZE = 50;

const AUDIT_ACTION_KEY: Record<SystemAuditAction, string> = {
  DEACTIVATE_USER: 'superAdmin.audit.action.deactivateUser',
  REACTIVATE_USER: 'superAdmin.audit.action.reactivateUser',
  PROMOTE_USER: 'superAdmin.audit.action.promoteUser',
  DEMOTE_USER: 'superAdmin.audit.action.demoteUser',
  CREATE_ADMIN: 'superAdmin.audit.action.createAdmin',
  ADMIN_SET_PASSWORD: 'superAdmin.audit.action.adminSetPassword',
  RESET_PASSWORD: 'superAdmin.audit.action.resetPassword',
  RETIRE_LOCATION: 'superAdmin.audit.action.retireLocation',
  REINSTATE_LOCATION: 'superAdmin.audit.action.reinstateLocation',
  CREATE_LOCATION: 'superAdmin.audit.action.createLocation',
  EDIT_LOCATION: 'superAdmin.audit.action.editLocation',
  SET_LOCATION_STATUS: 'superAdmin.audit.action.setLocationStatus',
  DELETE_USER: 'superAdmin.audit.action.deleteUser',
  DELETE_LOCATION: 'superAdmin.audit.action.deleteLocation',
  CREATE_JOB: 'superAdmin.audit.action.createJob',
  EDIT_JOB: 'superAdmin.audit.action.editJob',
  REASSIGN_JOB: 'superAdmin.audit.action.reassignJob',
  OVERRIDE_JOB_STATUS: 'superAdmin.audit.action.overrideJobStatus',
  CANCEL_JOB: 'superAdmin.audit.action.cancelJob',
  DELETE_JOB: 'superAdmin.audit.action.deleteJob',
  REVOKE_SESSION: 'superAdmin.audit.action.revokeSession',
  SET_SLA_POLICY: 'superAdmin.audit.action.setSlaPolicy',
  SET_ANALYTICS_BASELINE: 'superAdmin.audit.action.setAnalyticsBaseline',
};

/** Property time, not device time — a manager abroad must read the same clock as the floor. */
function formatWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: propertyTimeZone,
  }).format(at);
}

/**
 * The system audit trail, newest first and read-only.
 *
 * Read-only is the point: `SystemAuditLog` is append-only in the database, and a
 * console that could edit it would make every entry in it worthless as evidence.
 */
export function SuperAdminAudit() {
  const { t } = useTranslation();
  const [action, setAction] = useState<SystemAuditAction | ''>('');
  const [entries, setEntries] = useState<SystemAuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    setEntries(null);
    adminApi.superAdmin
      .audit({ action: action || undefined, limit: PAGE_SIZE })
      // Sorted here as well as server-side: the section promises newest first, and a
      // promise about ordering should not depend on a query plan staying the same.
      .then((rows) => setEntries([...rows].sort((a, b) => b.createdAt.localeCompare(a.createdAt))))
      .catch((caught: Error) => setError(caught.message));
  }, [action]);

  useEffect(load, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={action}
          onChange={(event) => setAction(event.target.value as SystemAuditAction | '')}
          aria-label={t('superAdmin.audit.filterAction')}
          className="min-h-[48px] flex-1 rounded border border-slate-700 bg-slate-900 px-2 text-sm"
        >
          <option value="">{t('superAdmin.audit.allActions')}</option>
          {systemAuditActionSchema.options.map((option) => (
            <option key={option} value={option}>
              {t(AUDIT_ACTION_KEY[option])}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={load}
          className="inline-flex min-h-[48px] items-center gap-1.5 rounded border border-slate-700 bg-slate-900 px-3 text-sm text-slate-200"
        >
          <RefreshCw className="size-3.5" aria-hidden="true" />
          {t('superAdmin.audit.refresh')}
        </button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {!error && !entries && <p className="text-sm text-slate-400">{t('superAdmin.loading')}</p>}

      {entries && entries.length === 0 && (
        <p className="text-sm text-slate-500">{t('superAdmin.audit.empty')}</p>
      )}

      {entries && entries.length > 0 && (
        <>
          <p className="text-xs text-slate-500">
            {t('superAdmin.audit.count', { count: entries.length })}
          </p>
          <ol className="divide-y divide-slate-800 rounded-xl border border-slate-800">
            {entries.map((entry) => (
              <AuditRow key={entry.id} entry={entry} />
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function AuditRow({ entry }: { entry: SystemAuditEntry }) {
  const { t } = useTranslation();
  const actionKey = AUDIT_ACTION_KEY[entry.action];

  return (
    <li className="p-3">
      <p className="flex flex-wrap items-baseline gap-x-2 text-sm text-slate-100">
        <span className="font-medium">{actionKey ? t(actionKey) : entry.action}</span>
        <span className="text-slate-300">{entry.targetLabel ?? entry.targetId}</span>
      </p>
      <p className="mt-0.5 text-xs text-slate-500">
        {t('superAdmin.audit.by', {
          // The actor's name is stored on the row, so it survives their account being
          // deactivated; null means the account was anonymised or the act was internal.
          actor: entry.actorName ?? entry.actorEmail ?? t('superAdmin.audit.unknownActor'),
          when: formatWhen(entry.createdAt),
        })}
      </p>
      <p className="mt-1 text-xs italic text-slate-400">
        {entry.reason ?? t('superAdmin.audit.noReason')}
      </p>
    </li>
  );
}
